import React, { useCallback, useEffect, useRef, useState } from 'react';
import { api, apiErrorMessage } from '../api/client';

export interface DocPage {
  n: number;
  mime_type: string;
  bytes: number;
}

export interface SourceDocument {
  document_id: string;
  kind: string;
  page_count: number;
  pages: DocPage[];
  uploaded_at: string | null;
  confirmed_at: string | null;
  dose_count: number;
}

export interface DoseRow {
  record_id: string;
  vaccine_name: string;
  vaccine_name_original?: string | null;
  cvx_code: string | null;
  dose_number: number;
  date_administered: string | null;
  date_administered_raw: string | null;
  source_calendar: string;
  lot_number: string | null;
  administered_by: string;
  verified?: boolean;
  verified_by?: string | null;
  verified_at?: string | null;
  verification_source?: string | null;
  rejected?: boolean;
  rejected_reason?: string | null;
  source_document_id: string | null;
}

interface Props {
  studentId: string;
  doc: SourceDocument;
  doses: DoseRow[];
  onClose: () => void;
}

const ZOOMS = [
  { label: 'Fit', value: 0 },
  { label: '100%', value: 1 },
  { label: '200%', value: 2 },
];

/**
 * Modal viewer for one guardian-submitted document (plan §13.7).
 * Pages are fetched with the staff JWT (never a bare <img src>), shown from
 * a blob: URL, and revoked on page change and close.
 */
const DocumentViewer: React.FC<Props> = ({ studentId, doc, doses, onClose }) => {
  const [page, setPage] = useState(1);
  const [url, setUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [zoom, setZoom] = useState(0);
  const [rotation, setRotation] = useState(0);
  const dialogRef = useRef<HTMLDivElement>(null);
  const closeRef = useRef<HTMLButtonElement>(null);

  const meta = doc.pages.find((p) => p.n === page) ?? doc.pages[0];
  const mime = meta?.mime_type ?? '';
  const isImage = /^image\/(jpeg|png|webp)$/.test(mime);
  const isPdf = mime === 'application/pdf';
  const isHeic = /^image\/hei[cf]$/.test(mime);

  useEffect(() => {
    let objectUrl: string | null = null;
    let cancelled = false;
    setUrl(null);
    setError(null);
    setZoom(0);
    setRotation(0);
    api
      .get(`/api/admin/students/${studentId}/documents/${doc.document_id}/pages/${page}`, {
        responseType: 'blob',
      })
      .then((res) => {
        if (cancelled) return;
        objectUrl = URL.createObjectURL(res.data as Blob);
        setUrl(objectUrl);
      })
      .catch((err) => !cancelled && setError(apiErrorMessage(err, 'Could not load this page.')));
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [studentId, doc.document_id, page]);

  // Focus trap + Esc to close.
  const onKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose();
        return;
      }
      if (e.key !== 'Tab' || !dialogRef.current) return;
      const focusable = dialogRef.current.querySelectorAll<HTMLElement>(
        'button:not([disabled]), a[href], iframe, [tabindex]:not([tabindex="-1"])'
      );
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    },
    [onClose]
  );

  useEffect(() => {
    closeRef.current?.focus();
  }, []);

  const linked = doses.filter((d) => d.source_document_id === doc.document_id);
  const label = `Page ${page} of ${doc.page_count}`;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/70 p-4">
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-label="Guardian-submitted record"
        onKeyDown={onKeyDown}
        className="flex h-[90vh] w-full max-w-6xl flex-col overflow-hidden rounded-xl bg-white shadow-xl"
      >
        <div className="flex flex-wrap items-center justify-between gap-2 border-b px-4 py-3">
          <div className="flex items-center gap-1" role="tablist" aria-label="Pages">
            {doc.pages.map((p) => (
              <button
                key={p.n}
                type="button"
                role="tab"
                aria-selected={p.n === page}
                onClick={() => setPage(p.n)}
                className={`rounded-md px-3 py-1 text-sm ${
                  p.n === page ? 'bg-blue-600 text-white' : 'border border-slate-300 text-slate-600'
                }`}
              >
                {p.n}
              </button>
            ))}
          </div>
          {isImage && (
            <div className="flex items-center gap-1 text-sm">
              {ZOOMS.map((z) => (
                <button
                  key={z.label}
                  type="button"
                  onClick={() => setZoom(z.value)}
                  className={`rounded-md px-2 py-1 ${zoom === z.value ? 'bg-slate-200' : 'hover:bg-slate-100'}`}
                >
                  {z.label}
                </button>
              ))}
              <button
                type="button"
                onClick={() => setRotation((r) => (r + 90) % 360)}
                className="rounded-md px-2 py-1 hover:bg-slate-100"
                title="Rotate 90°"
              >
                Rotate ↻
              </button>
            </div>
          )}
          <button
            ref={closeRef}
            type="button"
            onClick={onClose}
            className="rounded-md border border-slate-300 px-3 py-1 text-sm text-slate-600 hover:bg-slate-50"
          >
            Close
          </button>
        </div>

        <div className="flex min-h-0 flex-1">
          <div className="flex min-w-0 flex-1 items-center justify-center overflow-auto bg-slate-100 p-4">
            {error ? (
              <p className="text-sm text-red-600">{error}</p>
            ) : !url ? (
              <p className="text-sm text-slate-400">Loading…</p>
            ) : isImage ? (
              <img
                src={url}
                alt={label}
                style={{
                  transform: `rotate(${rotation}deg)`,
                  ...(zoom === 0
                    ? { maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }
                    : { width: `${zoom * 100}%`, maxWidth: 'none' }),
                }}
                className="transition-transform"
              />
            ) : isPdf ? (
              <iframe src={url} title={label} sandbox="allow-scripts" className="h-full w-full bg-white" />
            ) : (
              <div className="text-center text-sm text-slate-600">
                <p>
                  {isHeic
                    ? 'This photo is in HEIC format. Download to view it.'
                    : 'This file cannot be shown in the browser.'}
                </p>
                <a
                  href={url}
                  download={`record-${doc.document_id}-p${page}`}
                  className="mt-3 inline-block rounded-md bg-blue-600 px-4 py-2 font-semibold text-white"
                >
                  Download page {page}
                </a>
              </div>
            )}
          </div>

          <aside className="hidden w-72 shrink-0 overflow-auto border-l p-4 md:block">
            <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
              Doses from this document ({linked.length})
            </h3>
            {linked.length === 0 ? (
              <p className="text-sm text-slate-400">No doses linked.</p>
            ) : (
              <ul className="space-y-2 text-sm">
                {linked.map((d) => (
                  <li key={d.record_id} className="rounded-md border border-slate-200 p-2">
                    <div className="font-medium text-slate-800">
                      {d.vaccine_name} · dose {d.dose_number}
                    </div>
                    <div className="text-xs text-slate-500">
                      {d.date_administered || '—'}
                      {d.source_calendar !== 'gregorian' && d.date_administered_raw
                        ? ` (card: ${d.date_administered_raw})`
                        : ''}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </aside>
        </div>
      </div>
    </div>
  );
};

export default DocumentViewer;
